<template>
  <div class="dashboard-body-futuristic">

    <!-- Main Container -->
    <main class="futuristic-container" style="flex-direction: column; padding: 1rem 2rem;">

      <!-- Main Content (Risk Register View) -->
      <section class="dashboard-card-futuristic dashboard-card-scrollable" style="width: 100%;">
        <div class="pdf-viewer-wrapper">

          <Teleport to="#page-action-bar">
            <button class="btn-viewer-action btn-back" @click="goBack">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <h1 class="page-bar-title">Risk Register</h1>
            <div class="page-bar-actions">
              <div class="pdf-search-wrapper">
                <svg class="pdf-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  v-model="searchQuery"
                  type="text"
                  class="pdf-search-input"
                  placeholder="Search risks..."
                  @input="onSearchInput"
                />
                <button v-if="searchQuery" class="pdf-search-clear" @click="clearSearch" title="Clear search" aria-label="Clear search">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <button class="btn-viewer-action btn-download" @click="downloadProtected" :disabled="isDownloading" :class="{ 'disabled': isDownloading }">
                <svg v-if="!isDownloading" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <svg v-else class="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {{ isDownloading ? 'Downloading...' : 'Download Excel' }}
              </button>
              <button class="btn-viewer-action btn-download" @click="downloadMiniRiskRegister" :disabled="isDownloadingMini" :class="{ 'disabled': isDownloadingMini }" title="Download only the important GO/NO-GO risks">
                <svg v-if="!isDownloadingMini" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <svg v-else class="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {{ isDownloadingMini ? 'Downloading...' : 'Download Mini Risk' }}
              </button>
            </div>
          </Teleport>

          <div class="pdf-content-container table-responsive">
            <table class="pdf-content-table">
              <colgroup>
                <col style="width: 8%;">  <!-- Risk Area -->
                <col style="width: 11%;"> <!-- Subject of Risk -->
                <col style="width: 15%;"> <!-- Item Description -->
                <col style="width: 16%;"> <!-- Risk Description -->
                <col style="width: 9%;">  <!-- Chances of Occurrence -->
                <col style="width: 10%;"> <!-- Impact of Risk -->
                <col style="width: 7%;">  <!-- Severity -->
                <col style="width: 15%;"> <!-- Mitigation -->
                <col style="width: 9%;">  <!-- Ownership -->
              </colgroup>
              <thead>
                <tr>
                  <th>Risk Area</th>
                  <th>Subject of Risk</th>
                  <th>Item Description</th>
                  <th>Risk Description</th>
                  <th>Chances of Occurrence</th>
                  <th>Impact of Risk</th>
                  <th>Severity of Risk</th>
                  <th>Mitigation Measures</th>
                  <th>Responsibility/Ownership</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="filteredData.length">
                  <tr v-for="row in filteredData" :key="row.id">
                    <td><div class="cell-scroll" v-html="highlight(row.risk_area)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.subject_of_risk)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.item_description)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.risk_description)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.chances_of_occurrence)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.impact_of_risk)"></div></td>
                    <td :class="severityClass(row.severity_of_risk)"><div class="cell-scroll" v-html="highlight(row.severity_of_risk)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.mitigation_measures)"></div></td>
                    <td><div class="cell-scroll" v-html="highlight(row.responsibility_ownership)"></div></td>
                  </tr>
                </template>
                <tr v-else-if="searchQuery">
                  <td colspan="9" style="text-align:center; padding:2rem; color:#64748b;">
                    No results found for "<strong>{{ searchQuery }}</strong>"
                  </td>
                </tr>
                <tr v-else>
                  <td colspan="9" style="text-align:center; padding:2rem; color:#64748b;">
                    No risk register generated yet.
                  </td>
                </tr>
                <!-- colspan is 9 to match the 9 table columns above -->

              </tbody>
            </table>
          </div>

          <AppPagination
            v-if="!searchQuery"
            :currentPage="currentPage"
            :totalPages="totalPages"
            @page-changed="populate_risk_data"
          />
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import AppPagination from './AppPagination.vue';
import { notify } from '@/utils/notify';

export default {
  name: "RiskRegisterView",
  components: {
    AppPagination
  },

  props: {
    getAccessToken: Function,
  },

  data() {
    return {
      tableDataList: [],
      allTableData: [],
      currentPage: 1,
      totalPages: 1,
      searchQuery: '',
      searchTimeout: null,
      isDownloading: false,
      isDownloadingMini: false,
    };
  },

  computed: {
    filteredData() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.tableDataList;
      return this.allTableData.filter(row =>
        [row.risk_id_number, row.risk_area, row.subject_of_risk, row.item_description,
         row.risk_description, row.chances_of_occurrence, row.impact_of_risk,
         row.severity_of_risk, row.mitigation_measures, row.responsibility_ownership]
          .some(val => String(val || '').toLowerCase().includes(q))
      );
    },
  },

  mounted() {
    this.populate_risk_data();
  },

  methods: {
    getAuthHeaders() {
      return {
        Authorization: 'Bearer ' + this.getAccessToken()
      };
    },

    goBack() {
      // Back to the bid's Analysis tab; carry status/from so the sidebar keeps its highlight.
      const { status, from } = this.$route.query;
      this.$router.push({
        name: 'BidDetails',
        params: { pdf_id: this.$route.params.pdf_id },
        query: { tab: 'analysis', ...(status ? { status } : {}), ...(from ? { from } : {}) },
      });
    },

    // Colour the Severity of Risk cell by its rating, matching the Risk Matrix
    // legend: Critical=red, High=orange, Medium=yellow, Low=green.
    severityClass(severity) {
      const s = String(severity || '').trim().toLowerCase();
      if (s === 'critical') return 'sev-critical';
      if (s === 'high') return 'sev-high';
      if (s === 'medium') return 'sev-medium';
      if (s === 'low') return 'sev-low';
      return '';
    },

    downloadProtected() {
      // Full Risk Register (all risks) -> /download/risk-register
      return this.downloadExcel('/download/risk-register', 'risk-register.xlsx', 'isDownloading');
    },

    downloadMiniRiskRegister() {
      // Mini Risk Register (only the important GO/NO-GO risks) -> /download/mini-risk-register
      return this.downloadExcel('/download/mini-risk-register', 'mini-risk-register.xlsx', 'isDownloadingMini');
    },

    async downloadExcel(endpoint, fallbackName, spinnerFlag) {
      if (this[spinnerFlag]) return;
      this[spinnerFlag] = true;

      try {
        const pdf_id = this.$route.params.pdf_id;

        const res = await axios.get(this.$API_URL + endpoint + "?pdf_id=" + pdf_id, {
          responseType: 'blob',
          headers: this.getAuthHeaders(),
        });

        let filename = fallbackName;
        const disp = res.headers['content-disposition'] || res.headers['Content-Disposition'];
        if (disp) {
          const m = disp.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/i);
          if (m && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
        }

        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 100);
        }
      } catch (err) {
        console.error('Download failed', err);
        notify.error('Couldn’t download the file. Check your connection and try again.');
      } finally {
        this[spinnerFlag] = false;
      }
    },

    highlight(text) {
      const q = this.searchQuery.trim();
      if (!q || !text) return this.escapeHtml(String(text ?? ''));
      const escaped = this.escapeHtml(String(text));
      const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return escaped.replace(
        new RegExp(`(${escapedQ})`, 'gi'),
        '<mark class="search-highlight">$1</mark>'
      );
    },

    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        if (this.allTableData.length === 0) {
          this.fetchAllData();
        }
      }, 350);
    },

    clearSearch() {
      this.searchQuery = '';
    },

    fetchAllData() {
      const pdf_id = this.$route.params.pdf_id;
      if (!pdf_id) return;
      axios.get(this.$API_URL + `/user/risk-register-details?pdf_id=${pdf_id}&page=1&page_size=5000`, {
        headers: this.getAuthHeaders()
      })
        .then(res => {
          this.allTableData = res.data.items;
        })
        .catch(err => console.error(err));
    },

    populate_risk_data: function (page = 1) {
      const pdf_id = this.$route.params.pdf_id;
      if (!pdf_id) return;
      this.currentPage = page;

      axios.get(this.$API_URL + `/user/risk-register-details?pdf_id=${pdf_id}&page=${page}&page_size=10`, {
        headers: this.getAuthHeaders()
      })
        .then(res => {
          this.tableDataList = res.data.items;
          this.totalPages = res.data.total_pages;
          if (this.allTableData.length === 0) {
            this.fetchAllData();
          }
        })
        .catch(err => console.error(err));
    }
  }
};
</script>

<style scoped>
/* Light-blue table header. */
.pdf-content-table :deep(thead th) {
  background: #e0f2fe;
  color: #0c4a6e;
}

/* Severity of Risk cell colouring — matches the Risk Matrix legend.
   Use :deep so the background also covers the inner .cell-scroll wrapper. */
.sev-critical,
.sev-critical :deep(.cell-scroll) {
  background: #ff0000;
  color: #ffffff;
  font-weight: 700;
}

.sev-high,
.sev-high :deep(.cell-scroll) {
  background: #ffc000;
  color: #1e293b;
  font-weight: 700;
}

.sev-medium,
.sev-medium :deep(.cell-scroll) {
  background: #ffff00;
  color: #1e293b;
  font-weight: 700;
}

.sev-low,
.sev-low :deep(.cell-scroll) {
  background: #00b050;
  color: #ffffff;
  font-weight: 700;
}

.pdf-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.pdf-search-icon {
  position: absolute;
  left: 0.75rem;
  color: #64748b;
  pointer-events: none;
  flex-shrink: 0;
}

.pdf-search-input {
  padding: 0.5rem 2.2rem 0.5rem 2.2rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  background: #f8fafc;
  color: #1e293b;
  width: 220px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pdf-search-input:focus {
  border-color: #0071c5;
  box-shadow: 0 0 0 3px rgba(0, 113, 197, 0.12);
  background: #fff;
}

.pdf-search-input::placeholder {
  color: #64748b;
}

.pdf-search-clear {
  position: absolute;
  right: 0.55rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 0.2s;
}

.pdf-search-clear:hover {
  color: #ef4444;
}

:deep(.search-highlight) {
  background: #fef08a;
  color: #1e293b;
  border-radius: 4px;
  padding: 0 1px;
  font-weight: 600;
}

.spin {
  animation: spin 1s linear infinite;
}

.btn-download.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #64748b;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
